import styled from "@emotion/styled"
import { PLinkPure } from "@porsche-design-system/components-react"
import {
	radiusMd,
	colorCanvas,
	colorContrastLow,
	colorFrostedStrong,
	shadowSm,
	spacingStaticMd,
	spacingStaticLg,
	spacingStaticXs,
	spacingStaticSm,
} from "@porsche-design-system/components-react/emotion"
import { Link } from "react-router"
import { shouldForwardProp } from "src/common/shouldForwardProp"
import { NewBadge } from "../badge/NewBadge"
import { Tooltip } from "../tooltip/Tooltip"
import { DisabledItem } from "./DisabledItem"
import type { NavItem } from "./NavItem"

export interface SubmenuProps {
	insetInlineStart?: number
	menuContent: NavItem[]
	onExternalUrlClick: (item: NavItem) => void
	onMouseLeave: () => void
	pathname?: string
	top?: number
}

export const Submenu = ({
	insetInlineStart,
	menuContent,
	onExternalUrlClick,
	onMouseLeave,
	pathname,
	top,
	...rest
}: SubmenuProps) => (
	<SubmenuContainer $top={top} $insetInlineStart={insetInlineStart} onMouseLeave={onMouseLeave} {...rest}>
		{menuContent.map((item) => (
			<SubmenuItem key={item.key}>
				{item.isExternalUrl && typeof item.url === "string" ? (
					<PLinkPure
						icon="external"
						alignLabel="start"
						target="_blank"
						href={item.url}
						onClick={() => {
							onExternalUrlClick(item)
							onMouseLeave()
						}}
					>
						{item.label}
					</PLinkPure>
				) : typeof item.url === "object" ? (
					<Tooltip content={item.url.disabledHint}>
						<DisabledItem data-testid={`nav-submenu-${item.key}-disabled`}>{item.label}</DisabledItem>
					</Tooltip>
				) : (
					<ActiveLinkPure
						active={pathname === item.url || pathname?.startsWith(`${item.url}/`)}
						$active={pathname === item.url || pathname?.startsWith(`${item.url}/`)}
						icon="none"
						onClick={onMouseLeave}
					>
						<Link to={item.url} data-testid={`nav-submenu-${item.key}`}>
							{item.label}
						</Link>
					</ActiveLinkPure>
				)}
				{item.isNew && <NewBadge compact />}
			</SubmenuItem>
		))}
	</SubmenuContainer>
)

const SubmenuContainer = styled("div", { shouldForwardProp })<{ $insetInlineStart?: number; $top?: number }>`
	position: absolute;
	display: flex;
	flex-direction: column;
	gap: calc(${spacingStaticLg} + 1px); /* 1px for border/separator between items */
	background: ${colorCanvas};
	box-shadow: ${shadowSm};
	padding: calc(${spacingStaticMd} + ${spacingStaticXs}) ${spacingStaticMd};
	border: 1px solid ${colorContrastLow};
	border-radius: ${radiusMd};
	margin-top: ${spacingStaticMd};
	top: ${({ $top = 0 }) => $top}px;
	inset-inline-start: ${({ $insetInlineStart = 0 }) => $insetInlineStart}px;
	z-index: 5; /* PLinkTile on homescreen has z-index 1 and 4 internally and menu needs to be above it */

	&:after,
	&:before {
		bottom: 100%;
		inset-inline-start: 20px;
		border: solid transparent;
		content: "";
		height: 0;
		width: 0;
		position: absolute;
		pointer-events: none;
	}

	&:after {
		border-bottom-color: ${colorCanvas};
		border-width: 10px;
		margin-inline-start: -10px;
	}

	&:before {
		border-bottom-color: ${colorContrastLow};
		border-width: 11px;
		margin-inline-start: -11px;
	}
`

const SubmenuItem = styled.div`
	position: relative;
	display: flex;
	gap: ${spacingStaticSm};
	margin: 0 ${spacingStaticXs};
	
	/* separator line between items */
	&:not(:first-of-type):before {
		content: "";
		position: absolute;
		top: -${spacingStaticMd};
		display: block;
		height: 1px;
		left: -${spacingStaticXs};
		right: -${spacingStaticXs};
		background: ${colorContrastLow};
	}
`

// workaround until PLinkPure's active state has more contrast and matches links within PTabsBar
const ActiveLinkPure = styled(PLinkPure, { shouldForwardProp })<{ $active?: boolean }>`
	${({ $active }) => $active && `--p-color-frosted: ${colorFrostedStrong};`};
`
